import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SEO from '../components/seo/SEO';
import SeoPageIntro from '../components/seo/SeoPageIntro';
import { buildFaqSchema } from '../utils/seoStructuredData';
import { getFaqSeoEntries } from '../constants/faqSeoData';

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.6;
  color: var(--text-primary);
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const Question = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const Answer = styled.div`
  margin-bottom: 1rem;
  color: var(--text-secondary);
  
  p {
    margin-bottom: 0.75rem;
  }
  
  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 2rem 0;
`;

const FAQPage = () => {
  const { t } = useTranslation();
  const sections = t('faq.sections', { returnObjects: true });

  const renderBlock = (block, index) => (
    <React.Fragment key={index}>
      {(block.text || block.strong) && (
        <p>
          {block.strong && <strong>{block.strong}</strong>}
          {block.strong && block.text ? ' ' : null}
          {block.text}
        </p>
      )}
      {block.items && (
        <ul>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>
              {typeof item === 'string' ? item : (
                <>
                  {item.strong && <strong>{item.strong}</strong>}
                  {item.strong && item.text ? ' ' : null}
                  {item.text}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </React.Fragment>
  );

  return (
    <FAQContainer>
      <SEO
        title={t('faq.seoTitle')}
        description={t('faq.seoDescription')}
        keywords={t('faq.seoKeywords')}
        url="/faq"
        jsonLd={buildFaqSchema(getFaqSeoEntries(t))}
      />
      <SeoPageIntro
        h1={t('faq.h1')}
        description={t('faq.intro')}
      />
      {sections.map((section, index) => (
        <React.Fragment key={section.id}>
          <Section>
            <Question>{section.question}</Question>
            <Answer>{section.blocks.map(renderBlock)}</Answer>
          </Section>
          {index < sections.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </FAQContainer>
  );
};

export default FAQPage;
